"use client";

import clsx from "clsx";
import Link from "next/link";
import {useTranslation} from "react-i18next";
import {Icon} from "@/components/library/Icon";
import {SetupAction} from "@/utils/dashboard/setupActions";

type ActionType = "followUp" | "support" | "setup";

export interface ActionItem {
   id: string;
   type: ActionType;
   name: string;
   text: string;
   route?: string; // Optional navigation route for setup actions
   inProgress?: boolean;
}

interface RecommendedActionsPanelProps {
   actions?: ActionItem[];
   setupActions?: SetupAction[];
}

// Convert SetupAction to ActionItem format
function setupActionToActionItem(setupAction: SetupAction, t: (key: string) => string): ActionItem {
   return {
      id: setupAction.type,
      type: "setup",
      name: t(setupAction.titleKey),
      text: t(setupAction.descriptionKey),
      route: setupAction.route,
      inProgress: setupAction.isInProgress,
   };
}

function ActionTag({type, inProgress}: Readonly<{type: ActionType; inProgress?: boolean}>) {
   const {t} = useTranslation("stats");

   const tagConfig = {
      followUp: {
         className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
         label: t("recommendedActions.tags.followUp"),
      },
      support: {
         className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
         label: t("recommendedActions.tags.support"),
      },
      setup: {
         className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
         label: t("recommendedActions.tags.setup"),
      },
      inProgress: {
         className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
         label: t("recommendedActions.tags.inProgress"),
      },
   };

   const config = inProgress ? tagConfig.inProgress : tagConfig[type];

   return (
      <span
         className={clsx(
            "inline-flex",
            "items-center",
            "self-start",
            "px-2",
            "py-0.5",
            "rounded-full",
            "text-xs",
            "font-medium",
            config.className
         )}
      >
         {config.label}
      </span>
   );
}

function ActionCard({action}: Readonly<{action: ActionItem}>) {
   // Determine the href based on action type
   const href = action.route ?? (action.type === "setup" ? "/settings" : "/conversations");

   return (
      <Link
         href={href}
         className={clsx(
            "block",
            "p-4",
            "border",
            "border-neutral",
            "rounded-xl",
            "bg-panel",
            "transition-colors",
            "group",
            "hover:bg-gray-200 hover:dark:bg-gray-800"
         )}
      >
         <div className={clsx("flex", "items-start", "justify-between", "gap-2")}>
            <div className={clsx("flex", "flex-col", "gap-2", "flex-1", "min-w-0")}>
               <ActionTag type={action.type} inProgress={action.inProgress} />
               <p className={clsx("text-sm", "text-700", "line-clamp-2")}>
                  {action.name && (
                     <span className={clsx("font-semibold", "text-900")}>{action.name} </span>
                  )}
                  {action.text}
               </p>
            </div>
            {action.type === "setup" ? (
               <Icon
                  icon="sliders"
                  className={clsx(
                     "w-4",
                     "h-4",
                     "text-600",
                     "group-hover:text-800",
                     "transition-colors",
                     "flex-shrink-0",
                     "mt-1"
                  )}
               />
            ) : (
               <Icon
                  icon="arrow-right"
                  className={clsx(
                     "text-600",
                     "group-hover:text-800",
                     "transition-colors",
                     "flex-shrink-0",
                     "mt-1"
                  )}
               />
            )}
         </div>
      </Link>
   );
}

export default function RecommendedActionsPanel({
   setupActions = [],
}: Readonly<RecommendedActionsPanelProps>) {
   const {t} = useTranslation("stats");
   const {t: tOnboarding} = useTranslation("onboarding");

   // Convert setup actions to action items
   const currentActions: ActionItem[] = setupActions.map((action) =>
      setupActionToActionItem(action, tOnboarding)
   );

   // The dashboard renders this panel only when there is something to act on,
   // so an empty render here is a defensive no-op rather than an empty state.
   if (currentActions.length === 0) {
      return null;
   }

   return (
      <div className={clsx("flex", "flex-col", "h-full")}>
         {/* Header */}
         <h2 className={clsx("text-lg", "font-semibold", "text-900", "mb-2")}>
            {t("recommendedActions.title")}
         </h2>

         {/* Action Items */}
         <div className={clsx("flex", "flex-col", "gap-3", "flex-1", "overflow-y-auto")}>
            {currentActions.map((action) => (
               <ActionCard key={action.id} action={action} />
            ))}
         </div>

      </div>
   );
}
